<template>
    <div 
      v-click-outside="hideResults"
      class="tbx-map-box"
    >
      <b-spinner v-if="isLoading" class="loader" type="grow" label="Loading..."></b-spinner>      
      <input
        ref="tbxMapBox"
        v-model="searchInputText"
        :placeholder="placeholder"
        class="form-control"
        tabindex="-1"
        @input="getResultsDebounced"
        @click="showResults"
        @blur="onBlur"
        @focus="onFocus"
        @change="onChange"
        @keydown="onKeyDown" />
      <div
        v-show="resultsShown"
        tabindex="0"
        class="options"
      >
        <ul v-show="locationResults.length">
          <li
            v-for="(result, i) in locationResults"
            :key="result.id"
            :class="[{'arrow-select': i === arrowIndex}]"
            @click="setResult(result)"
          >
            {{result.place_name}}
          </li>
          <li
            v-if="locationResults.length > 0 && searchType === 'address'"
            @click="enterManually"
          >
            My location is not listed.
          </li>
        </ul>
        <p v-show="!locationResults.length">
          No results
        </p>
      </div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import axios from 'axios';
import { getGeocoderAPI } from '@/api';
import { mapBoxProxyToken } from '@/constants';
export default {
  props: {
    placeholder: {
      type: String,
      default: 'Enter a Location',
    },
    value: {
      type: String,
      default: '',
    },
    searchType: {
      type: String,
      default: 'address'
    }
  },
  data: () => ({
    searchInputText: '',
    resultsShown: false,
    locationResults: [],
    arrowIndex: -1,
    lastKeyCode: 0,
    currentSelection: {},
    mapBoxError: false,
    isLoading: false,
  }),
  async created() {
    this.getResultsDebounced = debounce(this.getResults, 250);
    this.searchInputText = this.value;
    // fetch map box object
    if (this.value) {
      await this.getUrlAddress()
    }
  },
  methods: {
    async getLocationResults(searchInputText) {
      const url = getGeocoderAPI(searchInputText, this.searchType);
      const results = await axios.get(url,
        {
          headers: { 'x-api-key': mapBoxProxyToken }
        }).catch(() => { this.mapBoxError = true });
      return results.data.features;
    },
    async getUrlAddress() {
      this.isLoading = true;
      const response = await this.getLocationResults(this.value) || [];
      if (response.length === 1) {
        this.$emit('on-location-selection', response[0]);
      } else if (response.length > 1) {
        this.locationResults = response
      } 
      this.isLoading = false;
    },
    async getResults() {
      if (!this.searchInputText) {
        this.locationResults = [];
        this.hideResults();
      } else {
        this.locationResults = await this.getLocationResults(this.searchInputText);
        this.showResults();   
      }
    },
    enterManually() {
      this.$emit('enter-manually');
    },
    onKeyDown (e) {
      switch (e.keyCode) {
        case 38:
          this.modifyArrowIndex(false);
          break;
        case 40:
          this.modifyArrowIndex(true);
          break;
        case 13:
          if (this.lastKeyCode === 13) {
            this.$emit('on-enter', JSON.parse(JSON.stringify(this.currentSelection)));
            this.resetArrowIndex()
          } else {
            this.setResult(this.locationResults[this.arrowIndex]);
          }
          break
        case 9:
          this.setResult(this.locationResults[this.arrowIndex])
      }
      this.lastKeyCode = e.keyCode
    },
    modifyArrowIndex (isDown) {
      if (isDown && this.arrowIndex < this.locationResults.length - 1) {
        this.arrowIndex = this.arrowIndex + 1;
      } else if (!isDown && this.arrowIndex >= 0) {
        this.arrowIndex = this.arrowIndex - 1;
      }
    },
    onChange () {
      if (this.searchInputText === '') {
        this.$emit('on-location-selection', {});
      }
      if(this.mapBoxError) {
        this.$emit('on-error', this.searchInputText);
      }
    },
    onBlur (e) {
      this.$emit('on-blur');
      this.handleBlur(e);
    },
    onFocus () {
      this.$emit('on-focus');
    },
    setResult (result) {
      if (result) {
        this.searchInputText = result.place_name
        this.resultsShown = false
        this.currentSelection = result
        this.$emit('on-location-selection', result)
        this.resetArrowIndex()
      } else {
        this.$emit('on-location-selection', {})
      }
    },
    hideResults() {
      this.resultsShown = false;
    },
    showResults() {
      if (this.searchInputText) {
        this.resultsShown = true;
      }
    },
    handleBlur(e) {
      const optionsContainerClicked = e.relatedTarget?.id === this.optionsContainerId;
      if (optionsContainerClicked || this.searchInputText) return;

      const result = {};
      this.setResult(result);
      this.resetArrowIndex()
      this.currentSelection = {}
    },
    resetArrowIndex () {
      this.arrowIndex = -1
    }
  }
}
</script>

<style>
.tbx-map-box {
  position:relative;
  text-align:left;
}
.tbx-map-box .loader {
  position:absolute;
  right: 0;
}
.tbx-map-box input {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.tbx-map-box input:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}
.tbx-map-box .options {
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  background-color: #fff;
  color: #000;
  border-radius: 0.375rem;
  width: 16rem;
  z-index: 10;
  position: absolute;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
.tbx-map-box .options ul {
  list-style: none;
  margin: 0;
  padding: 0;
}
.tbx-map-box .options ul li {
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
}
.tbx-map-box .options ul li:hover,
.tbx-map-box .options ul li.arrow-select {
  color: #fff;
  background-color: rgb(18, 159, 218);
}

</style>